
import React, { lazy, Suspense, useEffect } from 'react';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { useRealtimeSync } from '@/hooks/useRealtimeSync';
import { DataExportDialog } from '@/components/analytics/DataExport';
import { AnalyticsHeader } from '@/components/analytics/layout/AnalyticsHeader';
import { AnalyticsTabs } from '@/components/analytics/layout/AnalyticsTabs';
import { useUrlSyncedAnalyticsTab } from '@/hooks/useUrlSyncedAnalyticsTab';
import { ErrorBoundary } from 'react-error-boundary';
import { AnalyticsErrorFallback } from '@/components/analytics/layout/AnalyticsErrorFallback';

// Lazy-loaded components
const TrainingIntelligenceHub = lazy(() =>
  import('@/components/analytics/TrainingIntelligenceHub').then((m) => ({
    default: m.TrainingIntelligenceHub,
  }))
);
const EnhancedRunningAnalytics = lazy(() =>
  import('./EnhancedRunningAnalytics').then((m) => ({
    default: m.EnhancedRunningAnalytics,
  }))
);
const GeneralFitnessAnalytics = lazy(() =>
  import('./GeneralFitnessAnalytics').then((m) => ({
    default: m.GeneralFitnessAnalytics,
  }))
);
const StravaStatsWidget = lazy(() =>
  import('./StravaStatsWidget').then((m) => ({
    default: m.StravaStatsWidget,
  }))
);
const UniversalPerformanceAnalytics = lazy(() =>
  import('./UniversalPerformanceAnalytics').then((m) => ({
    default: m.UniversalPerformanceAnalytics,
  }))
);
const PerformanceCorrelationInsights = lazy(() =>
  import('./PerformanceCorrelationInsights').then((m) => ({
    default: m.PerformanceCorrelationInsights,
  }))
);
const InteractiveBodyCompositionChart = lazy(() =>
  import('./InteractiveBodyCompositionChart').then((m) => ({
    default: m.InteractiveBodyCompositionChart,
  }))
);
const CorrelationAnalysis = lazy(() =>
  import('./CorrelationAnalysis').then((m) => ({
    default: m.CorrelationAnalysis,
  }))
);
const TrendPrediction = lazy(() =>
  import('./TrendPrediction').then((m) => ({
    default: m.TrendPrediction,
  }))
);

export const AnalyticsDashboard: React.FC = () => {
  // Initialize realtime sync
  useRealtimeSync();
  // Tabs state (persisted)
  const { activeTab, setActiveTab } = useUrlSyncedAnalyticsTab();

  useEffect(() => {
    document.title = 'Advanced Analytics | Fitness Insights';
    const metaDesc =
      'Deep fitness analytics: training intelligence, running, performance, and body composition.';
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', metaDesc);

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', window.location.origin + '/analytics');
  }, []);

  return (
    <>
      <main className="h-full p-6 space-y-6 animate-fade-in overflow-auto" role="main">

        <AnalyticsHeader
          title="Advanced Analytics"
          description="Deep insights into your fitness journey with predictive analytics"
          actions={<DataExportDialog />}
        />

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full" aria-label="Analytics sections">
        <AnalyticsTabs />

          <TabsContent value="training-intelligence" className="space-y-4">
            <ErrorBoundary FallbackComponent={AnalyticsErrorFallback}>
              <Suspense fallback={<div className="h-40 animate-pulse bg-muted rounded-md" />}>
                <TrainingIntelligenceHub />
              </Suspense>
            </ErrorBoundary>

          </TabsContent>

          <TabsContent value="running" className="space-y-4">
            <ErrorBoundary FallbackComponent={AnalyticsErrorFallback}>
              <Suspense fallback={<div className="h-40 animate-pulse bg-muted rounded-md" />}>
                <EnhancedRunningAnalytics />
              </Suspense>
            </ErrorBoundary>

          </TabsContent>

          <TabsContent value="fitness" className="space-y-4">
            <ErrorBoundary FallbackComponent={AnalyticsErrorFallback}>
              <Suspense fallback={<div className="h-40 animate-pulse bg-muted rounded-md" />}>
                <GeneralFitnessAnalytics />
              </Suspense>
            </ErrorBoundary>

          </TabsContent>

          <TabsContent value="performance" className="space-y-4">
            <ErrorBoundary FallbackComponent={AnalyticsErrorFallback}>
              <Suspense fallback={<div className="h-40 animate-pulse bg-muted rounded-md" />}>
                <StravaStatsWidget />
                <UniversalPerformanceAnalytics />
                <PerformanceCorrelationInsights />
              </Suspense>
            </ErrorBoundary>

          </TabsContent>

          <TabsContent value="body" className="space-y-4">
            <ErrorBoundary FallbackComponent={AnalyticsErrorFallback}>
              <Suspense fallback={<div className="h-40 animate-pulse bg-muted rounded-md" />}>
                <InteractiveBodyCompositionChart />
                <CorrelationAnalysis />
                <TrendPrediction />
              </Suspense>
            </ErrorBoundary>

          </TabsContent>
        </Tabs>
      </main>
    </>
  );
};
